import { API_ENDPOINTS } from '../config/api';
import { userService } from '../_services/user.service';

// Verhindert parallele Refresh-Anfragen: alle wartenden Requests bekommen denselben neuen Token
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

async function refreshAccessToken() {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user || !user.refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: user.refreshToken })
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  const newToken = data.accessToken || data.token;

  if (!newToken) {
    throw new Error('No access token in refresh response');
  }

  const updatedUser = {
    ...user,
    token: newToken,
    refreshToken: data.refreshToken || user.refreshToken
  };
  localStorage.setItem('user', JSON.stringify(updatedUser));

  return newToken;
}

/**
 * fetch-Wrapper, der bei einem 401-Fehler automatisch den Access-Token per
 * Refresh-Token erneuert und die Anfrage einmal wiederholt.
 * Schlägt auch der Refresh fehl, wird der User ausgeloggt.
 *
 * Aufruf wie normales fetch(), nur ohne Authorization-Header setzen –
 * der wird hier anhand des gespeicherten Tokens automatisch ergänzt.
 *
 * @param {string} url
 * @param {RequestInit} options  – optionale fetch-Optionen (method, headers, body, …)
 */
export async function fetchWithAuth(url, options = {}) {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user || !user.token) {
    userService.logout();
    throw new Error('Not authenticated');
  }

  const buildHeaders = (token) => ({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`
  });

  const response = await fetch(url, { ...options, headers: buildHeaders(user.token) });

  if (response.status !== 401) {
    return response;
  }

  // --- 401: Token abgelaufen, Refresh versuchen ---

  if (isRefreshing) {
    // Andere Anfrage erneuert gerade – auf neuen Token warten
    return new Promise((resolve) => {
      addRefreshSubscriber((newToken) => {
        resolve(fetch(url, { ...options, headers: buildHeaders(newToken) }));
      });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await refreshAccessToken();
    isRefreshing = false;
    onRefreshed(newToken);
    // Ursprüngliche Anfrage mit neuem Token wiederholen
    return fetch(url, { ...options, headers: buildHeaders(newToken) });
  } catch (err) {
    isRefreshing = false;
    refreshSubscribers = [];
    console.warn('Token refresh failed – logging out', err);
    userService.logout();
    throw new Error('Session expired. Please log in again.');
  }
}
